import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@heroui/react';
import { api } from '@/lib/api';
import { onAppEvent } from '@/lib/appEvents';
import { useDataStore } from '@/stores/dataStore';
import { toast } from '@/lib/toast';
import { UserCard, type UserCardUser } from '@/components/userCard/UserCard';

/**
 * Opens a user's profile card when an @mention chip in a message is clicked
 * (markdown emits `mention:open` with the username). Resolves the username to
 * a user from the current DM's participants (no fetch) or the active guild's
 * members, then shows the shared {@link UserCard} in a centred modal. Mounted
 * once in the app shell.
 */
export function MentionProfileHost() {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserCardUser | null>(null);
  const [guildId, setGuildId] = useState<string | null>(null);

  const resolveAndOpen = useCallback(async (username: string) => {
    const target = username.toLowerCase();
    const s = useDataStore.getState();

    // DM context: resolve from the open conversation's participants (cheap).
    const dm = s.currentChannelId ? s.dmChannels[s.currentChannelId] : undefined;
    if (dm) {
      const p = dm.directParticipants.find((p) => p.user.username.toLowerCase() === target);
      if (p) {
        setGuildId(null);
        setUser({
          id: p.userId,
          username: p.user.username,
          displayName: p.user.displayName,
          avatarUrl: p.user.avatarUrl,
        });
        return;
      }
    }

    // Guild context: resolve from the server's member list.
    const gid = s.currentGuildId;
    if (gid) {
      try {
        const g = await api.getGuild(gid);
        const m = g.members.find((m) => m.user.username.toLowerCase() === target);
        if (m) {
          setGuildId(gid);
          setUser({
            id: m.user.id,
            username: m.user.username,
            displayName: m.user.displayName,
            avatarUrl: m.user.avatarUrl,
          });
          return;
        }
      } catch {
        /* fall through to the not-found toast */
      }
    }

    toast.error(t('prof.mention.notFound', { username }));
  }, [t]);

  // Re-subscribe when resolveAndOpen changes (i.e. on locale change) so the
  // not-found toast is never emitted through a stale `t`.
  useEffect(
    () => onAppEvent('mention:open', ({ username }) => void resolveAndOpen(username)),
    [resolveAndOpen],
  );

  function close() {
    setUser(null);
  }

  if (!user) return null;
  return (
    <Modal.Backdrop className="app-viewport-overlay" isOpen variant="blur" onOpenChange={(o) => !o && close()}>
      <Modal.Container size="lg" placement="center" className="app-profile-container">
        <Modal.Dialog aria-label={t('prof.card.profile')} className="app-profile-dialog w-[760px] max-w-[94vw] overflow-y-auto overflow-x-hidden p-0">
          <UserCard user={user} guildId={guildId} variant="dialog" onClose={close} />
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
